import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Stack } from "@mui/material";
import IconLoader from "@/features/Common/components/IconLoader";
import { panelStyle } from "@/theme";
import ScreenWrapper from "../../Common/ScreenWrapper";
import BloodPressureCard from "./InfoCard";
import BloodPressureChart, {
  BloodPressureDataPoint,
} from "./BloodPressureChart";
import CurrentSystolicBP from "./CurrentSystolicBP";
import CurrentDiastolicBP from "./CurrentDiastolicBP";
import HereToHelpCard from "../../HereToHelpCard";
import BloodPressureModal from "./BloodPressureModal";
import { WellnessScoreStatus } from "../../Common/types";

export interface MyBloodPressureScreenProps {
  chartData: BloodPressureDataPoint[];
  status: WellnessScoreStatus;
  lastUpdated?: string;
  /** When set, used for InfoCard and gauges instead of deriving from chartData (e.g. from health profile). */
  currentSystolic?: number;
  /** When set, used for InfoCard and gauges instead of deriving from chartData (e.g. from health profile). */
  currentDiastolic?: number;
  onBack?: () => void;
  onOpenManualEntry?: () => void;
}

export default function MyBloodPressureScreen({
  chartData,
  status,
  lastUpdated,
  currentSystolic: currentSystolicOverride,
  currentDiastolic: currentDiastolicOverride,
  onBack,
  onOpenManualEntry,
}: MyBloodPressureScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const fromChart =
    chartData.find((p) => p.isSelected) ?? chartData[chartData.length - 1];
  const systolicValue = currentSystolicOverride ?? fromChart?.systolic ?? 0;
  const diastolicValue = currentDiastolicOverride ?? fromChart?.diastolic ?? 0;

  const handleBack = onBack ?? (() => navigate(-1));
  const metricValue = `${systolicValue} / ${diastolicValue}`;
  const unit = t("BloodPressure.unit");

  return (
    <>
      <ScreenWrapper
        onBack={handleBack}
        title={t("HealthChart.bloodPressureModal.card.title")}
        description={t("HealthChart.bloodPressureModal.card.description")}
        onInfoClick={() => setIsInfoModalOpen(true)}
      >
        <Stack gap={2}>
          <BloodPressureCard
            metricValue={metricValue}
            status={status}
            lastUpdated={lastUpdated}
            onManualEntryClick={onOpenManualEntry}
          />
          <BloodPressureChart data={chartData} />
          <Box sx={{ ...panelStyle }}>
            <Stack gap={3}>
              <CurrentSystolicBP value={systolicValue} unit={unit} />
              <CurrentDiastolicBP value={diastolicValue} unit={unit} />
            </Stack>
          </Box>
          <HereToHelpCard
            title={t("HereToHelpCard.title")}
            description={t("HereToHelpCard.description")}
            button={{
              text: t("HereToHelpCard.button.text"),
              icon: <IconLoader icon="SpeakToADoctorIcon" />,
            }}
          />
        </Stack>
      </ScreenWrapper>

      <BloodPressureModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </>
  );
}
